import React, { useState, useEffect } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { commonToast } from "../../helper/Toast";
import Loading from "../Loading";
import { 
  selectSchool, 
  continueWithoutSchool, 
  setShowSchoolModal 
} from "../../redux/slices/schoolSlice";

export default function SchoolSelectionModal({
  isOpen,
  onClose,
  onSchoolSelect
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Get state from Redux
  const { 
    schoolIds, 
    availableSchools: schools, 
    loading, 
    selectedSchool 
  } = useSelector((state) => state.schoolData);

  const handleSchoolSelect = (school) => {
    dispatch(selectSchool(school));
  };

  const handleContinue = () => {
    if (selectedSchool) {
      onSchoolSelect(selectedSchool);
      onClose();
    } else if (schoolIds.length === 0) {
      dispatch(continueWithoutSchool());
      onSchoolSelect(null);
      onClose();
    } else {
      commonToast(t("pleaseSelectSchool"), "warning");
    }
  };

  
  return (
    <Dialog
      open={isOpen}
      as="div"
      className="relative z-30 focus:outline-none"
      onClose={() => onClose()}
    >
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 bg-black/35 backdrop-blur-sm">
          <DialogPanel className="w-full relative max-w-2xl rounded-3xl bg-white duration-300 ease-out">
            <div className="p-6 bg-white relative z-10 rounded-3xl">
                              <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t("selectSchool")}
                  </h2>
                  <IoMdClose
                    className="cursor-pointer text-gray-400 hover:text-gray-600"
                    fontSize={24}
                    onClick={onClose}
                  />
                </div>

             

              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <Loading />
                </div>
              ) : schoolIds.length === 0 ? (
                <div className="text-center py-8">
                  <div className="mb-4">
                    <svg 
                      className="mx-auto h-12 w-12 text-gray-400" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" 
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t("noSchoolsFound")}
                  </h3>
                  <p className="text-gray-600">
                    {t("noSchoolsMessage")}
                  </p>
                </div>
              ) : (
                <div className="space-y-3 mb-6">
                  {schools.length === 1 && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700">
                        {t("singleSchoolAutoSelected")}
                      </p>
                    </div>
                  )}
                  {schools.map((school) => (
                    <div
                      key={school.id}
                      className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
                        selectedSchool?.id === school.id
                          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                      onClick={() => handleSchoolSelect(school)}
                    >
                      <div className="flex items-center justify-between">
                        {/* School Info with Logo */}
                        <div className="flex items-center gap-4 flex-1">
                          {school.smallLogoUrl ? (
                            <img
                              src={school.smallLogoUrl}
                              alt={`${school.schoolName} logo`}
                              className="w-12 h-12 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="text-md font-semibold text-gray-800">
                              {school.schoolName || `School ID: ${school.id}`}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {[
                                school.city,
                                school.countyProvinceState,
                                school.country,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </p>
                          </div>
                        </div>

                        {/* Selection Indicator */}
                        {selectedSchool?.id === school.id && (
                          <div className="ml-4 flex-shrink-0">
                            <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center ring-4 ring-blue-200">
                              <svg
                                className="w-4 h-4 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t("cancel")}
                </button>
                <button
                  onClick={handleContinue}
                  disabled={loading}
                  className={`px-6 py-2 rounded-lg transition-colors ${
                    (selectedSchool || schoolIds.length === 0) && !loading
                      ? "bg-blue-500 text-white hover:bg-blue-600"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {t("continue")}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
} 